'use client';

// ---------------------------------------------------------------------------
// OfflineIndicator — Status badge for online/offline + sync queue
// ---------------------------------------------------------------------------
// Displays in the Topbar action area. Shows:
//   - Green dot: Online, all synced
//   - Amber dot + count: Online, syncing queued mutations
//   - Red dot: Offline, with queued mutation count
// ---------------------------------------------------------------------------

import { cn } from '@/core/utils/cn';
import { useOfflineStore } from '@/core/stores/offline.store';
import { Wifi, WifiOff, RefreshCw, CloudOff } from 'lucide-react';
import { useTranslation } from '@/core/i18n';

interface OfflineIndicatorProps {
  id?: string;
  className?: string;
  /** Show only the compact dot indicator (no text) */
  compact?: boolean;
}

export function OfflineIndicator({
  id = 'offline-indicator',
  className,
  compact = false,
}: OfflineIndicatorProps) {
  const { isOnline, queueCount, syncStatus } = useOfflineStore();
  const { t } = useTranslation();

  // Online + no queue = don't show anything (clean state)
  if (isOnline && queueCount === 0 && syncStatus !== 'syncing') {
    return (
      <div
        id={id}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg',
          'text-hms-green/70',
          className
        )}
        title={t('shared.offlineIndicator.online')}
      >
        <Wifi size={14} strokeWidth={2} />
        {!compact && (
          <span className="text-[11px] font-medium hidden lg:inline">{t('shared.offlineIndicator.online')}</span>
        )}
      </div>
    );
  }

  // Syncing state
  if (isOnline && (syncStatus === 'syncing' || queueCount > 0)) {
    return (
      <div
        id={id}
        className={cn(
          'flex items-center gap-1.5 px-2 py-1.5 rounded-lg',
          'text-hms-amber bg-hms-amber/5',
          className
        )}
        title={t('shared.offlineIndicator.syncing') + (queueCount > 0 ? ` ${queueCount} ${queueCount === 1 ? t('shared.offlineIndicator.queuedOperation') : t('shared.offlineIndicator.queuedOperations')}` : '')}
      >
        <RefreshCw size={14} strokeWidth={2} className="animate-spin" />
        {!compact && (
          <span className="text-[11px] font-medium hidden lg:inline">
            {t('shared.offlineIndicator.syncing')}{queueCount > 0 ? ` (${queueCount})` : ''}
          </span>
        )}
        {compact && queueCount > 0 && (
          <span className="text-[10px] font-bold">{queueCount}</span>
        )}
      </div>
    );
  }

  // Offline state
  return (
    <div
      id={id}
      className={cn(
        'flex items-center gap-1.5 px-2 py-1.5 rounded-lg',
        'text-hms-red bg-hms-red/5',
        className
      )}
      title={queueCount > 0 ? t('shared.offlineIndicator.offlineQueued', { count: String(queueCount) }) : t('shared.offlineIndicator.offline')}
    >
      {queueCount > 0 ? (
        <CloudOff size={14} strokeWidth={2} />
      ) : (
        <WifiOff size={14} strokeWidth={2} />
      )}
      {!compact && (
        <span className="text-[11px] font-medium hidden lg:inline">
          {t('shared.offlineIndicator.offline')}{queueCount > 0 ? ` (${queueCount})` : ''}
        </span>
      )}
      {compact && queueCount > 0 && (
        <span className="text-[10px] font-bold">{queueCount}</span>
      )}
    </div>
  );
}
